import { supabase } from '../clients/supabaseClient';

export type EmployeeRole = 'driver' | 'picker';

export type Employee = {
    auth_id: string;
    first_name: string;
    last_name: string;
    cellphone: string;
    roles: EmployeeRole[];
    driver_id?: string;
    driver_store_id?: string;
    driver_store_name?: string;
    car_model?: string;
    number_plate?: string;
    picker_id?: string;
    picker_store_id?: string;
    picker_store_name?: string;
};

type DriverRow = {
    id: string;
    auth_id: string;
    store_id: string;
    car_model: string;
    number_plate: string;
    store?: { id: string; name: string } | null;
};

type PickerRow = {
    id: string;
    auth_id: string;
    store_id: string;
    store?: { id: string; name: string } | null;
};

type ProfileRow = {
    id: string;
    first_name: string;
    last_name: string | null;
    cellphone: string | null;
};

// Fetches all active employees (drivers and pickers) and merges them into a unified list.
// Does a two-step fetch: drivers and pickers in parallel, then a single batched profiles
// lookup by auth_id to get names and contact details. A person who is both a driver and
// a picker appears as a single Employee record with both roles in the roles array.
// Results are sorted alphabetically by full name.
export async function fetchEmployeesFromDb(): Promise<Employee[]> {
    const [driversRes, pickersRes] = await Promise.all([
        supabase
            .from('drivers')
            .select('id, auth_id, store_id, car_model, number_plate, store:stores(id, name)')
            .is('deleted_at', null),
        supabase
            .from('pickers')
            .select('id, auth_id, store_id, store:stores(id, name)')
            .is('deleted_at', null),
    ]);

    if (driversRes.error) throw driversRes.error;
    if (pickersRes.error) throw pickersRes.error;

    const drivers = (driversRes.data ?? []) as unknown as DriverRow[];
    const pickers = (pickersRes.data ?? []) as unknown as PickerRow[];

    const authIds = Array.from(
        new Set([...drivers.map(d => d.auth_id), ...pickers.map(p => p.auth_id)].filter(Boolean))
    );

    if (authIds.length === 0) return [];

    const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, cellphone')
        .in('id', authIds);

    if (profilesError) throw profilesError;

    const profiles = (profilesData ?? []) as ProfileRow[];
    const profileById = new Map(profiles.map(p => [p.id, p]));

    const byAuthId = new Map<string, Employee>();

    for (const d of drivers) {
        const profile = profileById.get(d.auth_id);
        const existing = byAuthId.get(d.auth_id);
        const base: Employee = existing ?? {
            auth_id: d.auth_id,
            first_name: profile?.first_name ?? '',
            last_name: profile?.last_name ?? '',
            cellphone: profile?.cellphone ?? '',
            roles: [],
        };
        base.roles = Array.from(new Set([...base.roles, 'driver' as EmployeeRole]));
        base.driver_id = d.id;
        base.driver_store_id = d.store_id;
        base.driver_store_name = d.store?.name;
        base.car_model = d.car_model;
        base.number_plate = d.number_plate;
        byAuthId.set(d.auth_id, base);
    }

    for (const p of pickers) {
        const profile = profileById.get(p.auth_id);
        const existing = byAuthId.get(p.auth_id);
        const base: Employee = existing ?? {
            auth_id: p.auth_id,
            first_name: profile?.first_name ?? '',
            last_name: profile?.last_name ?? '',
            cellphone: profile?.cellphone ?? '',
            roles: [],
        };
        base.roles = Array.from(new Set([...base.roles, 'picker' as EmployeeRole]));
        base.picker_id = p.id;
        base.picker_store_id = p.store_id;
        base.picker_store_name = p.store?.name;
        byAuthId.set(p.auth_id, base);
    }

    return Array.from(byAuthId.values()).sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    );
}

// Updates a staff member's profile fields (name, cellphone) in the profiles table.
// Keyed by auth_id (profiles.id) rather than the driver/picker row ID.
export async function updateEmployeeProfileInDb(
    authId: string,
    patch: { first_name?: string; last_name?: string; cellphone?: string }
): Promise<void> {
    const { error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', authId);

    if (error) throw error;
}

export type AddPickerRolePayload = {
    auth_id: string;
    first_name: string;
    last_name: string;
    store_id: string;
};

// Adds the picker role to a staff member. Because pickers.auth_id has a unique constraint,
// a plain insert would conflict if the role was previously removed (soft-deleted).
// So we check for an existing row first — if found, we restore it by clearing deleted_at
// and updating the details; otherwise we insert fresh.
export async function addPickerRoleInDb(payload: AddPickerRolePayload): Promise<void> {
    // A picker row may already exist for this auth_id if the role was
    // previously removed (soft-deleted) — pickers.auth_id is unique, so
    // inserting a new row would conflict with the old one. Restore it
    // instead of inserting fresh.
    const { data: existing, error: existingErr } = await supabase
        .from('pickers')
        .select('id')
        .eq('auth_id', payload.auth_id)
        .maybeSingle();

    if (existingErr) throw existingErr;

    if (existing) {
        const { error } = await supabase
            .from('pickers')
            .update({
                deleted_at: null,
                first_name: payload.first_name,
                last_name:  payload.last_name,
                store_id:   payload.store_id,
            })
            .eq('id', existing.id);

        if (error) throw error;
        return;
    }

    const { error } = await supabase
        .from('pickers')
        .insert([{
            auth_id:    payload.auth_id,
            first_name: payload.first_name,
            last_name:  payload.last_name,
            store_id:   payload.store_id,
        }]);

    if (error) throw error;
}

export type AddDriverRolePayload = {
    auth_id: string;
    first_name: string;
    last_name: string;
    store_id: string;
    car_model: string;
    number_plate: string;
};

// Adds the driver role to a staff member. Uses the same restore-if-exists pattern as
// addPickerRoleInDb — drivers.auth_id is unique so a previously-removed driver row
// is restored (deleted_at cleared) rather than re-inserted to avoid constraint conflicts.
export async function addDriverRoleInDb(payload: AddDriverRolePayload): Promise<void> {
    // Same restore-if-exists logic as addPickerRoleInDb — drivers.auth_id
    // is unique, so a previously-removed driver role would still have a
    // row sitting there with deleted_at set.
    const { data: existing, error: existingErr } = await supabase
        .from('drivers')
        .select('id')
        .eq('auth_id', payload.auth_id)
        .maybeSingle();

    if (existingErr) throw existingErr;

    if (existing) {
        const { error } = await supabase
            .from('drivers')
            .update({
                deleted_at:   null,
                driver_name:  `${payload.first_name} ${payload.last_name}`,
                store_id:     payload.store_id,
                car_model:    payload.car_model,
                number_plate: payload.number_plate,
            })
            .eq('id', existing.id);

        if (error) throw error;
        return;
    }

    const { error } = await supabase
        .from('drivers')
        .insert([{
            auth_id:      payload.auth_id,
            driver_name:  `${payload.first_name} ${payload.last_name}`,
            store_id:     payload.store_id,
            car_model:    payload.car_model,
            number_plate: payload.number_plate,
        }]);

    if (error) throw error;
}

// Soft-deletes a picker role by stamping deleted_at, but only after two safety checks:
// 1. No IN_PROGRESS picking session — removing a picker mid-pick would orphan the order.
// 2. No orders currently sitting at PACKER_ASSIGNED with this picker's model_id in
//    order_status_history — the order would have no way to be resolved from the picker app.
// Both checks throw a user-facing error so the admin knows what needs resolving first.
export async function removePickerRoleInDb(pickerId: string): Promise<void> {
    // Block removal if this picker has anything currently assigned to
    // them — either an order still sitting at PACKER_ASSIGNED (not yet
    // started) or an IN_PROGRESS picking session. Removing the role out
    // from under active work would orphan the order with no way for
    // anyone to resolve it from the picker app.
    const { data: picker, error: pickerErr } = await supabase
        .from('pickers')
        .select('id, auth_id')
        .eq('id', pickerId)
        .single();

    if (pickerErr) throw pickerErr;

    const { data: activeSessions, error: sessionsErr } = await supabase
        .from('picking_sessions')
        .select('id')
        .eq('picker_id', pickerId)
        .eq('status', 'IN_PROGRESS')
        .limit(1);

    if (sessionsErr) throw sessionsErr;

    if ((activeSessions ?? []).length > 0) {
        throw new Error(
            'This picker has an order currently in progress. They must finish or cancel it before this role can be removed.'
        );
    }

    const { data: packerAssignedStatus, error: statusErr } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', 'PACKER_ASSIGNED')
        .maybeSingle();

    if (statusErr) throw statusErr;

    if (packerAssignedStatus) {
        // Find orders currently sitting at PACKER_ASSIGNED whose most
        // recent picker-assignment history row points at this picker.
        // Mirrors the exact lookup used in the start-picking-session
        // edge function, just run here in reverse (picker -> orders).
        const { data: assignmentRows, error: assignmentsErr } = await supabase
            .from('order_status_history')
            .select('order_id, created_at')
            .eq('model_type', 'Picker')
            .eq('model_id', picker.id)
            .eq('status_id', packerAssignedStatus.id)
            .order('created_at', { ascending: false });

        if (assignmentsErr) throw assignmentsErr;

        const candidateOrderIds = Array.from(new Set((assignmentRows ?? []).map(r => r.order_id)));

        if (candidateOrderIds.length > 0) {
            const { data: stillAssignedOrders, error: ordersErr } = await supabase
                .from('orders')
                .select('id')
                .in('id', candidateOrderIds)
                .eq('status', 'PACKER_ASSIGNED');

            if (ordersErr) throw ordersErr;

            if ((stillAssignedOrders ?? []).length > 0) {
                throw new Error(
                    'This picker still has an order assigned to them for picking. Reassign or complete it first before removing this role.'
                );
            }
        }
    }

    const { error } = await supabase
        .from('pickers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', pickerId);

    if (error) throw error;
}

// Soft-deletes a driver role by stamping deleted_at. The driver record is retained
// so historical deliveries and assignments remain intact and traceable.
export async function removeDriverRoleInDb(driverId: string): Promise<void> {
    const { error } = await supabase
        .from('drivers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', driverId);

    if (error) throw error;
}